import api from '../api/client';
// components/PayablesAnalytics.js
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Spinner, Alert, ProgressBar, Table } from 'react-bootstrap';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import config from '../config';
import { formatKES } from '../utils/currency';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend);

const PayablesAnalytics = ({ dateRange, fetchData }) => {
    const [metrics, setMetrics] = useState(null);
    const [vendorData, setVendorData] = useState([]);
    const [detailedData, setDetailedData] = useState([]);
    const [summaryData, setSummaryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const environment = window.location.hostname === 'localhost' ? 'development' : 'production';
    const apiUrl = config[environment].apiUrl;
    const [payablesData, setPayablesData] = useState([]);

    useEffect(() => {
        fetchData('payables', setPayablesData);
    }, [dateRange, fetchData]);
const data = payablesData;

    useEffect(() => {
        loadPayablesData();
    }, [dateRange]);

    const loadPayablesData = async () => {
        setLoading(true);
        setError(null);

        try {
            const [metricsRes, vendorRes, detailedRes, summaryRes] = await Promise.all([
                fetch(`/api/analytics/payables-metrics?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`),
                fetch(`/api/analytics/payables-by-vendor?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`),
                fetch(`/api/analytics/payables-detailed?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`),
                fetch(`/api/analytics/expenses-summary?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`)
            ]);

            if (!metricsRes.ok || !vendorRes.ok || !detailedRes.ok || !summaryRes.ok) {
                throw new Error('Failed to fetch payables data');
            }

            const [metricsData, vendorData, detailedData, summaryData] = await Promise.all([
                metricsRes.json(),
                vendorRes.json(),
                detailedRes.json(),
                summaryRes.json()
            ]);

            setMetrics(metricsData);
            setVendorData(vendorData);
            setDetailedData(detailedData);
            setSummaryData(summaryData);
        } catch (err) {
            console.error('Error loading payables:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top' },
            tooltip: {
                callbacks: {
                    label: (context) => `KES ${context.raw.toLocaleString()}`
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: { callback: (value) => `KES ${value.toLocaleString()}` }
            }
        }
    };

    const vendorChartData = {
        labels: vendorData.map((item) => item.vendor_category),
        datasets: [
            {
                label: 'Total Amount',
                data: vendorData.map((item) => item.total_amount),
                backgroundColor: 'rgba(54, 162, 235, 0.6)'
            },
            {
                label: 'Paid Amount',
                data: vendorData.map((item) => item.paid_amount),
                backgroundColor: 'rgba(75, 192, 192, 0.6)'
            },
            {
                label: 'Balance Due',
                data: vendorData.map((item) => item.balance_amount),
                backgroundColor: 'rgba(255, 99, 132, 0.6)'
            }
        ]
    };

    // const chartOptions = {
    //   responsive: true,
    //   maintainAspectRatio: false,
    //   plugins: {
    //     legend: { position: 'top' },
    //     tooltip: {
    //       callbacks: {
    //         label: (context) => `KES ${context.raw.toLocaleString()}`
    //       }
    //     }
    //   },
    //   scales: {
    //     y: {
    //       beginAtZero: true,
    //       ticks: { callback: (value) => `KES ${value.toLocaleString()}` }
    //     }
    //   }
    // };

    // const categoryChartData = {
    //   labels: categoryData.map(item => item.category),
    //   datasets: [
    //     {
    //       label: 'Total Amount',
    //       data: categoryData.map(item => item.total_amount),
    //       backgroundColor: 'rgba(54, 162, 235, 0.6)',
    //     },
    //     {
    //       label: 'Paid Amount',
    //       data: categoryData.map(item => item.paid_amount),
    //       backgroundColor: 'rgba(75, 192, 192, 0.6)',
    //     },
    //     {
    //       label: 'Balance Amount',
    //       data: categoryData.map(item => item.balance_amount),
    //       backgroundColor: 'rgba(255, 99, 132, 0.6)',
    //     }
    //   ]
    // };

    // Prepare chart data
    // const trendChartData = {
    //   labels: trendData?.monthlyBreakdown?.map(item => item.month) || [],
    //   datasets: [
    //     {
    //       label: 'Total payables',
    //       data: trendData?.monthlyBreakdown?.map(item => item.total) || [],
    //       borderColor: 'rgb(75, 192, 192)',
    //       backgroundColor: 'rgba(75, 192, 192, 0.2)',
    //       tension: 0.1,
    //       fill: true,
    //     }
    //   ]
    // };

    if (!payablesData || payablesData.length === 0) {
        return <div className="text-center py-4">No payables data available</div>;
    }

    // if (!data) return <p>Loading...</p>;
    // if (loading) return <LoadingSpinner />;
    // if (error) return <ErrorAlert error={error} onRetry={loadAnalyticsData} />;
    // if (!metrics) return <NoDataMessage />;

    const barData = {
        labels: Object.keys(data.breakdown.byStakeholder),
        datasets: [
            {
                label: 'Balance',
                data: Object.values(data.breakdown.byStakeholder).map((s) => s.balance),
                backgroundColor: '#e74a3b'
            },
            {
                label: 'Paid',
                data: Object.values(data.breakdown.byStakeholder).map((s) => s.paid),
                backgroundColor: '#1cc88a'
            }
        ]
    };

    if (loading) return <LoadingSpinner />;
    if (error) return <ErrorAlert error={error} onRetry={loadPayablesData} />;
    if (!metrics) return <NoDataMessage type="payables" />;

    return (
        <>
            <Row className="mb-4">
                <Col md={3} className="mb-3">
                    <MetricCard
                        title="Total Payables"
                        value={metrics.total_amount}
                        subValue={`${metrics.total_bills} bills`}
                        icon="fas fa-file-invoice-dollar"
                        color="primary"
                    />
                </Col>
                <Col md={3} className="mb-3">
                    <MetricCard
                        title="Amount Paid"
                        value={metrics.total_paid}
                        subValue={`${Math.round((metrics.total_paid / metrics.total_amount) * 100)}% paid`}
                        icon="fas fa-money-check"
                        color="success"
                    />
                </Col>
                <Col md={3} className="mb-3">
                    <MetricCard
                        title="Balance Due"
                        value={metrics.total_balance}
                        subValue={`${metrics.overdue_count} overdue`}
                        icon="fas fa-clock"
                        color="warning"
                    />
                </Col>
                <Col md={3} className="mb-3">
                    <MetricCard
                        title="Overdue Amount"
                        value={metrics.overdue_amount}
                        subValue={`${Math.round(metrics.avg_days_overdue)} days avg.`}
                        icon="fas fa-exclamation-triangle"
                        color="danger"
                    />
                </Col>
            </Row>

            {/* Progress Bar */}
            <Row className="mb-4">
                <Col md={12}>
                    <Card>
                        <Card.Header>
                            <h6 className="mb-0">Payment Progress</h6>
                        </Card.Header>
                        <Card.Body>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Payment Rate</span>
                                <span>{Math.round((metrics.total_paid / metrics.total_amount) * 100)}%</span>
                            </div>
                            <ProgressBar
                                now={(metrics.total_paid / metrics.total_amount) * 100}
                                variant="success"
                                className="mb-3"
                                style={{ height: '20px' }}
                            />
                            <Row>
                                <Col md={4}>
                                    <div className="text-center">
                                        <h6 className="text-success">KES {metrics.total_paid?.toLocaleString()}</h6>
                                        <small className="text-muted">Amount Paid</small>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="text-center">
                                        <h6 className="text-warning">KES {metrics.total_balance?.toLocaleString()}</h6>
                                        <small className="text-muted">Balance Due</small>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="text-center">
                                        <h6 className="text-danger">KES {metrics.overdue_amount?.toLocaleString()}</h6>
                                        <small className="text-muted">Overdue</small>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Vendor Breakdown */}
            <Row className="mb-4">
                <Col md={12}>
                    <Card>
                        <Card.Header>
                            <h6 className="mb-0">Payables by Category</h6>
                        </Card.Header>
                        <Card.Body>
                            <div style={{ height: '400px' }}>
                                <Bar data={vendorChartData} options={chartOptions} />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Refresh Button */}
            <Row>
                {/* <Col md={6} className="mb-3">
                <Card>
                   <h5>Payables Trend Over Time</h5>
                    <div style={{ height: '300px' }}>
                      <Line 
                        data={trendChartData} 
                        options={chartOptions}
                      />
                    </div>
                </Card>
              </Col> */}

                <Col md={12}>
                    <Card>
                        <h5>Payables by Stakeholder</h5>
                        <div style={{ height: '400px' }}>
                            <Bar data={barData} />
                        </div>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

// Helper Components (same as before)
const LoadingSpinner = () => (
    <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading payables data...</p>
    </div>
);

const ErrorAlert = ({ error, onRetry }) => (
    <Alert variant="danger">
        <Alert.Heading>Error Loading Payables</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" onClick={onRetry}>
            Retry
        </Button>
    </Alert>
);

const NoDataMessage = ({ type = 'payables' }) => (
    <div className="text-center py-4">
        <i className="fas fa-file-invoice-dollar fa-3x text-muted mb-3"></i>
        <h5>No {type} data available</h5>
        <p className="text-muted">No {type} data found for the selected period.</p>
    </div>
);

const MetricCard = ({ title, value, subValue, icon, color }) => (
    <Card className={`text-center border-${color}`}>
        <Card.Body>
            <div className={`text-${color} mb-2`}>
                <i className={`${icon} fa-2x`}></i>
            </div>
            <h5 className={`text-${color}`}>{formatKES(value) || 0}</h5>
            <p className="text-muted mb-0">{title}</p>
            {subValue && <small className="text-muted">{subValue}</small>}
        </Card.Body>
    </Card>
);

export default PayablesAnalytics;
